import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Lock, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * TrialGateBanner — persistent banner shown on trades/journal pages when
 * the caller is post-trial + unsubscribed. Data past the last 7 trading
 * sessions is hidden by the backend automatically; this component just
 * makes the user aware that filtering is in effect and offers a subscribe
 * CTA. Chart data + order-panel functionality are NOT affected (per spec).
 */

// Routes where restricted data is actually rendered. Off-scope on
// prop-challenge / charts / settings so users don't get banner-spam on
// pages that never touch the trades collection.
const GATED_ROUTES = [
  "/trades",
  "/analytics",
  "/strategies",
  "/dashboard",
];

export default function TrialGateBanner() {
  const location = useLocation();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me/access").then(r => {
      if (!cancelled) setStatus(r.data);
    }).catch(() => { if (!cancelled) setStatus({ restricted: false }); });
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (!status || !status.restricted) return null;
  const isGatedRoute = GATED_ROUTES.some(p =>
    location.pathname === p || location.pathname.startsWith(p + "/")
  );
  if (!isGatedRoute) return null;

  return (
    <div
      className="mx-4 mt-3 flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5"
      data-testid="trial-gate-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Lock size={16} weight="bold" className="text-amber-500 shrink-0" />
        <p className="text-xs sm:text-sm text-amber-100">
          Showing last <span className="font-semibold">{status.session_limit || 7} trading sessions</span> only.
          <span className="text-amber-200/80"> Subscribe to unlock full history.</span>
        </p>
      </div>
      <Link
        to="/billing"
        data-testid="trial-gate-banner-cta"
        className="inline-flex items-center gap-1 rounded-md bg-amber-500 hover:bg-amber-400 text-neutral-900 text-xs font-semibold px-3 py-1.5 shrink-0 transition"
      >
        Subscribe <ArrowRight size={12} weight="bold" />
      </Link>
    </div>
  );
}
